import { useCallback, useEffect, useId, useMemo, useState } from "react";
import type { FormEvent } from "react";
import { PageHeader } from "@/components/PageHeader";
import { AreaTabs, FARM_TABS } from "@/components/AreaTabs";
import { StateMessage } from "@/components/StateMessage";
import { Why } from "@/components/Why";
import { DialogShell } from "@/components/DialogShell";
// ef-063 anchor: the Loads card (a stub until ef-063-1b-4 REPLACES the component file; the line below stays).
import { GrainLoadsPanel } from "@/components/GrainLoadsPanel";
import { demoApi, problemFieldErrors, problemMessage } from "@/api/client";
import { useSelectedEntity } from "@/hooks/useSelectedEntity";
import { grainBinLabel } from "@/lib/grainBinLabel";
import type {
  CreateGrainBinRequest,
  CreateGrainContractRequest,
  CreateGrainDeliveryRequest,
  GrainBin,
  GrainContract,
  GrainLocationKind,
  GrainUnit,
  Guid,
  Vendor,
} from "@/api/types";
import "./Grain.css";

/**
 * ef-046-4 — the owner's Grain screen, `/farm/grain`: bins (with on-hand by crop, an
 * "Add bin" form), forward-sale contracts (delivered/remaining and a derived status —
 * open/partial/fulfilled), and a "Record a delivery" form (bin, contract, qty, price,
 * Paid/Not-yet-paid — D8). Calls ef-046-1's/`-2`'s/`-3`'s routes
 * (`GrainBinEndpoints.cs`/`GrainContractEndpoints.cs`/`GrainDeliveryEndpoints.cs`)
 * directly — `api/client.ts`'s own `// ---- ef-046-1: bins ----`/
 * `// ---- ef-046-4: contracts and deliveries ----` sections. Per ADR 0009's "one page
 * template" — `PageHeader` (title only; each card below carries its own one primary
 * action) → the Farm area tab strip → content in cards.
 *
 * NO SEPARATE DIALOG/ROW COMPONENT FILE — this job's own file scope names only
 * `Grain.tsx`/`Grain.css`, so every dialog and row lives here as a local, unexported
 * component, same posture `Fields.tsx` already takes for its own scope.
 *
 * NO ARCHIVE ROUTE ON BINS, NO EDIT ROUTE CALLED ON CONTRACTS, NO DELIVERY LIST —
 * `GrainBinEndpoints.cs` ships no archive/restore this wave, `GrainDeliveryEndpoints.cs`
 * ships no GET this wave, and `Grain.tsx`'s own file scope covers create + settle only —
 * flagged in this job's own return contract rather than invented here.
 *
 * BUYER IS A VENDOR — the "New contract" dialog's own buyer picker is `demoApi
 * .getVendors()`, not a grain-specific lookup (`GrainContractEndpoints.cs`'s own
 * remarks: "a grain buyer is a vendor, not a new lookup table").
 */

type GrainStatus = "loading" | "ready" | "error";

const UNIT_OPTIONS: { value: GrainUnit; label: string }[] = [
  { value: "bu", label: "Bushels" },
  { value: "ton", label: "Tons" },
  { value: "cwt", label: "CWT" },
];

function capitalize(value: string): string {
  return value.length === 0 ? value : value[0]!.toUpperCase() + value.slice(1);
}

export function Grain() {
  // ef-047-1 — replaces this screen's own `entities[0]` guess with the shared, persisted
  // switcher. `load` below closes over `entityId` (it's a dependency, not a param, since
  // this hook never took one) so every existing `onCreated`/`onRecorded` call site keeps
  // calling `load()` unchanged; `entityStatus` gates the mount effect exactly like this
  // screen always waited for its own `getEntities()` call to resolve first.
  const { entityId, status: entityStatus } = useSelectedEntity();
  const [bins, setBins] = useState<GrainBin[] | null>(null);
  const [contracts, setContracts] = useState<GrainContract[] | null>(null);
  const [vendors, setVendors] = useState<Vendor[] | null>(null);
  const [status, setStatus] = useState<GrainStatus>("loading");
  const [error, setError] = useState<string | null>(null);
  const [binDialogOpen, setBinDialogOpen] = useState(false);
  const [contractDialogOpen, setContractDialogOpen] = useState(false);
  const [deliveryDialogOpen, setDeliveryDialogOpen] = useState(false);

  /** Re-reads the whole screen — bins, contracts, and the vendor pick-list — same
   *  "wholesale reload after every write, never patch arrays in place" convention
   *  `Fields()`'s own `load()` documents for itself, so a bin's on-hand and a contract's
   *  delivered/remaining/status never drift from what the server actually holds. */
  const load = useCallback(async (): Promise<void> => {
    setStatus("loading");
    setError(null);
    try {
      const [binList, contractList, vendorList] = await Promise.all([
        demoApi.listGrainBins(entityId ?? undefined),
        demoApi.getGrainContracts(entityId ?? undefined),
        demoApi.getVendors(),
      ]);
      setBins(binList);
      setContracts(contractList);
      setVendors(vendorList);
      setStatus("ready");
    } catch (err) {
      setError(problemMessage(err, "Could not load grain."));
      setStatus("error");
    }
  }, [entityId]);

  useEffect(() => {
    if (entityStatus === "loading") return;
    void load();
  }, [load, entityStatus]);

  const activeBins = useMemo(
    () => [...(bins ?? [])].filter((b) => b.archivedAt == null).sort((a, b) => a.name.localeCompare(b.name)),
    [bins],
  );
  const sortedContracts = useMemo(
    () => [...(contracts ?? [])].sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
    [contracts],
  );
  const vendorNameById = useMemo(() => {
    const map = new Map<Guid, string>();
    for (const v of vendors ?? []) map.set(v.id, v.name);
    return map;
  }, [vendors]);

  const canRecordDelivery = activeBins.length > 0 && sortedContracts.length > 0;

  return (
    <main className="grain-page">
      <PageHeader title="Grain" subtitle="Bins, forward-sale contracts, and the deliveries settled against them." />

      <AreaTabs area="/farm" tabs={FARM_TABS} label="Farm" />

      <section
        className="grain-page__card"
        aria-labelledby="grain-bins-heading"
        data-state={status === "ready" && activeBins.length === 0 ? "empty" : status}
      >
        <div className="grain-page__card-head">
          <h2 id="grain-bins-heading" className="grain-page__card-title">
            Bins
          </h2>
          <button type="button" className="btn btn--primary" onClick={() => setBinDialogOpen(true)}>
            New bin
          </button>
        </div>

        <Why>
          A bin's on-hand balance, one line per crop, is derived from every harvest and
          delivery posted against it — never entered or stored directly.
        </Why>

        {status === "loading" && <StateMessage tone="loading" title="Loading bins…" />}

        {status === "error" && (
          <StateMessage tone="error" title="Could not load bins." detail={error ?? undefined} />
        )}

        {status === "ready" && activeBins.length === 0 && (
          <StateMessage
            tone="empty"
            title="No bins yet."
            detail="Add your first bin to start tracking on-hand grain."
          />
        )}

        {status === "ready" && activeBins.length > 0 && (
          <ul className="grain-page__list" aria-label="Bins">
            {activeBins.map((bin) => (
              <BinRow key={bin.id} bin={bin} />
            ))}
          </ul>
        )}
      </section>

      <section
        className="grain-page__card"
        aria-labelledby="grain-contracts-heading"
        data-state={status === "ready" && sortedContracts.length === 0 ? "empty" : status}
      >
        <div className="grain-page__card-head">
          <h2 id="grain-contracts-heading" className="grain-page__card-title">
            Contracts
          </h2>
          <button type="button" className="btn btn--primary" onClick={() => setContractDialogOpen(true)}>
            New contract
          </button>
        </div>

        <Why>
          Delivered, remaining, and status are computed from every delivery posted
          against a contract — open until the first delivery, partial until deliveries
          reach the full quantity, then fulfilled.
        </Why>

        {status === "loading" && <StateMessage tone="loading" title="Loading contracts…" />}

        {status === "error" && (
          <StateMessage tone="error" title="Could not load contracts." detail={error ?? undefined} />
        )}

        {status === "ready" && sortedContracts.length === 0 && (
          <StateMessage
            tone="empty"
            title="No contracts yet."
            detail="Add a forward-sale contract with a buyer."
          />
        )}

        {status === "ready" && sortedContracts.length > 0 && (
          <ul className="grain-page__list" aria-label="Contracts">
            {sortedContracts.map((c) => (
              <ContractRow key={c.id} contract={c} buyerName={vendorNameById.get(c.buyerId) ?? "Buyer"} />
            ))}
          </ul>
        )}
      </section>

      <section className="grain-page__card" aria-labelledby="grain-deliveries-heading">
        <div className="grain-page__card-head">
          <h2 id="grain-deliveries-heading" className="grain-page__card-title">
            Deliveries
          </h2>
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => setDeliveryDialogOpen(true)}
            disabled={!canRecordDelivery}
          >
            Record a delivery
          </button>
        </div>

        <Why>
          A delivery moves grain out of a bin against a contract, in the contract's own
          unit — recorded Paid (posts a balanced entry now) or Not yet paid (posts an
          invoice instead).
        </Why>

        {status === "ready" && !canRecordDelivery && (
          <StateMessage
            tone="empty"
            title="Add a bin and a contract first."
            detail="A delivery needs both to record against."
          />
        )}
      </section>

      {/* ef-063 anchor - scale loads (ef-063-1b-4 REPLACES the component; this line stays). */}
      <GrainLoadsPanel entityId={entityId} bins={activeBins} onChanged={() => void load()} />

      {binDialogOpen && (
        <NewBinDialog
          entityId={entityId}
          vendors={vendors ?? []}
          onClose={() => setBinDialogOpen(false)}
          onCreated={() => void load()}
        />
      )}
      {contractDialogOpen && (
        <NewContractDialog
          entityId={entityId}
          vendors={vendors ?? []}
          onClose={() => setContractDialogOpen(false)}
          onCreated={() => void load()}
        />
      )}
      {deliveryDialogOpen && (
        <RecordDeliveryDialog
          bins={activeBins}
          contracts={sortedContracts}
          vendorNameById={vendorNameById}
          onClose={() => setDeliveryDialogOpen(false)}
          onRecorded={() => void load()}
        />
      )}
    </main>
  );
}

function BinRow({ bin }: { bin: GrainBin }) {
  return (
    <li className="grain-page__row" data-bin-id={bin.id} data-location={bin.locationKind ?? "farm"}>
      <span className="grain-page__meta">
        <span className="grain-page__tag">{bin.name}</span>
        <span className="grain-page__sub">{bin.capacity != null ? `${bin.capacity} capacity` : "—"}</span>
        {bin.locationKind === "elevator" && bin.vendorName && (
          <span className="grain-page__sub">At {bin.vendorName}</span>
        )}
      </span>

      {bin.onHand.length === 0 ? (
        <p className="grain-page__sub">No grain on hand.</p>
      ) : (
        <ul className="grain-page__onhand-list" aria-label={`${bin.name} on-hand by crop`}>
          {bin.onHand.map((oh) => (
            <li key={oh.crop} className="grain-page__onhand-line">
              {oh.crop}: {oh.qty.toFixed(2)}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

function ContractRow({ contract, buyerName }: { contract: GrainContract; buyerName: string }) {
  return (
    <li className="grain-page__row" data-contract-id={contract.id} data-state={contract.status}>
      <span className="grain-page__meta">
        <span className="grain-page__tag">
          {contract.crop} — {buyerName}
        </span>
        <span className="grain-page__sub">
          {contract.qty.toFixed(2)} {contract.unit} at{" "}
          <span className="grain-page__money">${contract.pricePerUnit.toFixed(2)}</span>/{contract.unit}
        </span>
      </span>

      <span className="grain-page__contract-status">
        <span className="grain-page__status">{capitalize(contract.status)}</span>
        <span className="grain-page__sub">
          Delivered {contract.delivered.toFixed(2)} · Remaining {contract.remaining.toFixed(2)}
        </span>
      </span>
    </li>
  );
}

function NewBinDialog({
  entityId,
  vendors,
  onClose,
  onCreated,
}: {
  entityId: Guid | null;
  vendors: Vendor[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState("");
  const [capacity, setCapacity] = useState("");
  // ef-063-1c-c: where the bin is. "farm" is the default and sends neither field (the pre-0089 wire body);
  // "elevator" requires a vendor and sends both.
  const [locationKind, setLocationKind] = useState<GrainLocationKind>("farm");
  const [vendorId, setVendorId] = useState("");
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]> | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const formId = useId();
  const nameId = useId();
  const capacityId = useId();
  const locationId = useId();
  const vendorFieldId = useId();

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setFieldErrors(null);
    setFormError(null);

    /* @ui-check: client-side ONLY — GrainBinEndpoints.cs's own ValidateName/
       ValidateCapacity still run server-side and remain the actual contract. This guard
       exists so a blank name never leaves the browser, same posture NewFieldDialog's own
       handleSubmit takes. */
    const trimmedName = name.trim();
    if (trimmedName === "") {
      setFieldErrors({ name: ["Name is required."] });
      return;
    }
    const capacityValue = capacity.trim() === "" ? undefined : Number(capacity);
    if (capacityValue !== undefined && (!Number.isFinite(capacityValue) || capacityValue <= 0)) {
      setFieldErrors({ capacity: ["Must be greater than zero."] });
      return;
    }
    /* @ui-check: an elevator bin with no vendor never leaves the browser (the server's 400 naming `vendorId` is
       the real contract); a farm bin sends neither `locationKind` nor `vendorId`. */
    if (locationKind === "elevator" && vendorId === "") {
      setFieldErrors({ vendorId: ["Choose the elevator that holds this bin."] });
      return;
    }
    if (entityId === null) {
      setFormError("No farm entity found for this tenant yet.");
      return;
    }

    setSaving(true);
    try {
      const body: CreateGrainBinRequest = { entityId, name: trimmedName, capacity: capacityValue };
      if (locationKind === "elevator") {
        body.locationKind = "elevator";
        body.vendorId = vendorId;
      }
      await demoApi.createGrainBin(body);
      onCreated();
      onClose();
    } catch (err) {
      setFieldErrors(problemFieldErrors(err));
      setFormError(problemMessage(err, "Could not create this bin."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <DialogShell
      title="New bin"
      onClose={onClose}
      busy={saving}
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" form={formId} className="btn btn--primary" disabled={saving}>
            {saving ? "…" : "Add bin"}
          </button>
        </>
      }
    >
      <form id={formId} className="grain-page__dialog-form" onSubmit={(e) => void handleSubmit(e)} noValidate>
        <span className="grain-page__field">
          <label htmlFor={nameId}>Name</label>
          <input
            id={nameId}
            type="text"
            maxLength={80}
            value={name}
            onChange={(e) => setName(e.target.value)}
            aria-invalid={fieldErrors?.name ? true : undefined}
          />
          {fieldErrors?.name && (
            <p className="grain-page__field-error" role="alert">
              {fieldErrors.name[0]}
            </p>
          )}
        </span>

        <span className="grain-page__field">
          <label htmlFor={capacityId}>Capacity (optional)</label>
          <input
            id={capacityId}
            type="number"
            min="0.01"
            step="0.01"
            value={capacity}
            onChange={(e) => setCapacity(e.target.value)}
            aria-invalid={fieldErrors?.capacity ? true : undefined}
          />
          {fieldErrors?.capacity && (
            <p className="grain-page__field-error" role="alert">
              {fieldErrors.capacity[0]}
            </p>
          )}
        </span>

        <span className="grain-page__field">
          <label htmlFor={locationId}>Where is it stored?</label>
          <select
            id={locationId}
            value={locationKind}
            onChange={(e) => {
              setLocationKind(e.target.value as GrainLocationKind);
              setFieldErrors(null);
            }}
          >
            <option value="farm">On the farm</option>
            <option value="elevator">At an elevator</option>
          </select>
        </span>

        {locationKind === "elevator" && (
          <span className="grain-page__field">
            <label htmlFor={vendorFieldId}>Elevator</label>
            <select
              id={vendorFieldId}
              value={vendorId}
              onChange={(e) => setVendorId(e.target.value)}
              aria-invalid={fieldErrors?.vendorId ? true : undefined}
            >
              <option value="">Select an elevator…</option>
              {vendors.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </select>
            {fieldErrors?.vendorId && (
              <p className="grain-page__field-error" role="alert">
                {fieldErrors.vendorId[0]}
              </p>
            )}
          </span>
        )}

        {formError && !fieldErrors?.name && !fieldErrors?.capacity && !fieldErrors?.vendorId && (
          <p className="grain-page__dialog-error" role="alert">
            {formError}
          </p>
        )}

      </form>
    </DialogShell>
  );
}

function NewContractDialog({
  entityId,
  vendors,
  onClose,
  onCreated,
}: {
  entityId: Guid | null;
  vendors: Vendor[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const [buyerId, setBuyerId] = useState("");
  const [crop, setCrop] = useState("");
  const [qty, setQty] = useState("");
  const [pricePerUnit, setPricePerUnit] = useState("");
  const [unit, setUnit] = useState<GrainUnit>("bu");
  const [basis, setBasis] = useState("");
  const [futuresMonth, setFuturesMonth] = useState("");
  const [deliveryStart, setDeliveryStart] = useState("");
  const [deliveryEnd, setDeliveryEnd] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const formId = useId();
  const buyerFieldId = useId();
  const cropId = useId();
  const qtyId = useId();
  const priceId = useId();
  const unitId = useId();
  const basisId = useId();
  const futuresMonthId = useId();
  const startId = useId();
  const endId = useId();

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);

    /* @ui-check: client-side ONLY — GrainContractEndpoints.cs's own ValidateCrop/
       ValidateQty/ValidatePricePerUnit/ValidateUnit/ValidateFuturesMonth and the
       delivery-window check still run server-side and remain the actual contract. */
    if (buyerId === "") {
      setError("Select a buyer.");
      return;
    }
    const trimmedCrop = crop.trim();
    if (trimmedCrop === "") {
      setError("Crop is required.");
      return;
    }
    const qtyValue = Number(qty);
    if (!Number.isFinite(qtyValue) || qtyValue <= 0) {
      setError("Quantity must be greater than zero.");
      return;
    }
    const priceValue = Number(pricePerUnit);
    if (!Number.isFinite(priceValue) || priceValue <= 0) {
      setError("Price per unit must be greater than zero.");
      return;
    }
    if (deliveryStart === "" || deliveryEnd === "") {
      setError("Delivery start and end are both required.");
      return;
    }
    if (deliveryEnd < deliveryStart) {
      setError("Delivery end must be on or after delivery start.");
      return;
    }
    if (entityId === null) {
      setError("No farm entity found for this tenant yet.");
      return;
    }

    setSaving(true);
    try {
      const basisValue = basis.trim() === "" ? undefined : Number(basis);
      const body: CreateGrainContractRequest = {
        entityId,
        buyerId,
        crop: trimmedCrop,
        qty: qtyValue,
        pricePerUnit: priceValue,
        unit,
        basis: basisValue,
        futuresMonth: futuresMonth.trim() === "" ? undefined : futuresMonth.trim(),
        deliveryStart,
        deliveryEnd,
      };
      await demoApi.createGrainContract(body);
      onCreated();
      onClose();
    } catch (err) {
      // A 404 here means `buyerId` did not resolve on this tenant — surfaced as the same
      // inline sentence every other failure uses, never a silent no-op
      // (`GrainContractEndpoints.cs`'s own remarks).
      setError(problemMessage(err, "Could not create this contract."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <DialogShell
      title="New contract"
      onClose={onClose}
      busy={saving}
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" form={formId} className="btn btn--primary" disabled={saving}>
            {saving ? "…" : "Add contract"}
          </button>
        </>
      }
    >
      <form id={formId} className="grain-page__dialog-form" onSubmit={(e) => void handleSubmit(e)} noValidate>
        <span className="grain-page__field">
          <label htmlFor={buyerFieldId}>Buyer</label>
          <select id={buyerFieldId} value={buyerId} onChange={(e) => setBuyerId(e.target.value)}>
            <option value="">Select a buyer…</option>
            {vendors.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </select>
        </span>

        <span className="grain-page__field">
          <label htmlFor={cropId}>Crop</label>
          <input id={cropId} type="text" maxLength={80} value={crop} onChange={(e) => setCrop(e.target.value)} />
        </span>

        <span className="grain-page__field">
          <label htmlFor={qtyId}>Quantity</label>
          <input
            id={qtyId}
            type="number"
            min="0.01"
            step="0.01"
            value={qty}
            onChange={(e) => setQty(e.target.value)}
          />
        </span>

        <span className="grain-page__field">
          <label htmlFor={priceId}>Price per unit</label>
          <input
            id={priceId}
            type="number"
            min="0.01"
            step="0.01"
            className="grain-page__money-input"
            value={pricePerUnit}
            onChange={(e) => setPricePerUnit(e.target.value)}
          />
        </span>

        <span className="grain-page__field">
          <label htmlFor={unitId}>Unit</label>
          <select id={unitId} value={unit} onChange={(e) => setUnit(e.target.value as GrainUnit)}>
            {UNIT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </span>

        <span className="grain-page__field">
          <label htmlFor={basisId}>Basis (optional)</label>
          <input id={basisId} type="number" step="0.01" value={basis} onChange={(e) => setBasis(e.target.value)} />
        </span>

        <span className="grain-page__field">
          <label htmlFor={futuresMonthId}>Futures month (optional)</label>
          <input
            id={futuresMonthId}
            type="text"
            maxLength={40}
            value={futuresMonth}
            onChange={(e) => setFuturesMonth(e.target.value)}
          />
        </span>

        <span className="grain-page__field">
          <label htmlFor={startId}>Delivery start</label>
          <input
            id={startId}
            type="date"
            value={deliveryStart}
            onChange={(e) => setDeliveryStart(e.target.value)}
          />
        </span>

        <span className="grain-page__field">
          <label htmlFor={endId}>Delivery end</label>
          <input id={endId} type="date" value={deliveryEnd} onChange={(e) => setDeliveryEnd(e.target.value)} />
        </span>

        {error && (
          <p className="grain-page__dialog-error" role="alert">
            {error}
          </p>
        )}

      </form>
    </DialogShell>
  );
}

function RecordDeliveryDialog({
  bins,
  contracts,
  vendorNameById,
  onClose,
  onRecorded,
}: {
  bins: GrainBin[];
  contracts: GrainContract[];
  vendorNameById: Map<Guid, string>;
  onClose: () => void;
  onRecorded: () => void;
}) {
  const [binId, setBinId] = useState("");
  const [contractId, setContractId] = useState("");
  const [qty, setQty] = useState("");
  const [pricePerUnit, setPricePerUnit] = useState("");
  const [paid, setPaid] = useState<boolean | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const formId = useId();
  const binFieldId = useId();
  const contractFieldId = useId();
  const qtyId = useId();
  const priceId = useId();

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);

    /* @ui-check: client-side ONLY — GrainDeliveryEndpoints.cs's own ValidateQty/
       ValidatePricePerUnit checks still run server-side and remain the actual contract.
       The 409 for insufficient on-hand / a missing chart account is NEVER guessed at
       client-side (this form does not re-derive GrainBinRepository's own on-hand math) —
       it is surfaced inline from the server's own response below, via
       `problemMessage`. */
    if (binId === "") {
      setError("Select a bin.");
      return;
    }
    if (contractId === "") {
      setError("Select a contract.");
      return;
    }
    const qtyValue = Number(qty);
    if (!Number.isFinite(qtyValue) || qtyValue <= 0) {
      setError("Quantity must be greater than zero.");
      return;
    }
    const priceValue = Number(pricePerUnit);
    if (!Number.isFinite(priceValue) || priceValue <= 0) {
      setError("Price per unit must be greater than zero.");
      return;
    }
    if (paid === null) {
      setError("Choose Paid or Not yet paid.");
      return;
    }

    setSaving(true);
    try {
      const body: CreateGrainDeliveryRequest = {
        binId,
        contractId,
        qty: qtyValue,
        pricePerUnit: priceValue,
        paid,
      };
      await demoApi.createGrainDelivery(body);
      onRecorded();
      onClose();
    } catch (err) {
      // 409 (insufficient on-hand / missing chart account) lands here, inline, and
      // changes nothing — see this function's own @ui-check above.
      setError(problemMessage(err, "Could not record this delivery."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <DialogShell
      title="Record a delivery"
      onClose={onClose}
      busy={saving}
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" form={formId} className="btn btn--primary" disabled={saving}>
            {saving ? "…" : "Record delivery"}
          </button>
        </>
      }
    >
      <form id={formId} className="grain-page__dialog-form" onSubmit={(e) => void handleSubmit(e)} noValidate>
        <span className="grain-page__field">
          <label htmlFor={binFieldId}>Bin</label>
          <select id={binFieldId} value={binId} onChange={(e) => setBinId(e.target.value)}>
            <option value="">Select a bin…</option>
            {bins.map((b) => (
              <option key={b.id} value={b.id}>
                {grainBinLabel(b)}
              </option>
            ))}
          </select>
        </span>

        <span className="grain-page__field">
          <label htmlFor={contractFieldId}>Contract</label>
          <select id={contractFieldId} value={contractId} onChange={(e) => setContractId(e.target.value)}>
            <option value="">Select a contract…</option>
            {contracts.map((c) => (
              <option key={c.id} value={c.id}>
                {c.crop} — {vendorNameById.get(c.buyerId) ?? "Buyer"} ({c.remaining.toFixed(2)} {c.unit} left)
              </option>
            ))}
          </select>
        </span>

        <span className="grain-page__field">
          <label htmlFor={qtyId}>Quantity</label>
          <input
            id={qtyId}
            type="number"
            min="0.01"
            step="0.01"
            value={qty}
            onChange={(e) => setQty(e.target.value)}
          />
        </span>

        <span className="grain-page__field">
          <label htmlFor={priceId}>Price per unit</label>
          <input
            id={priceId}
            type="number"
            min="0.01"
            step="0.01"
            className="grain-page__money-input"
            value={pricePerUnit}
            onChange={(e) => setPricePerUnit(e.target.value)}
          />
        </span>

        <fieldset className="grain-page__paid-group">
          <legend>Payment</legend>
          <label className="grain-page__radio">
            <input type="radio" name="paid" checked={paid === true} onChange={() => setPaid(true)} />
            <span>Paid</span>
          </label>
          <label className="grain-page__radio">
            <input type="radio" name="paid" checked={paid === false} onChange={() => setPaid(false)} />
            <span>Not yet paid</span>
          </label>
        </fieldset>

        {error && (
          <p className="grain-page__dialog-error" role="alert">
            {error}
          </p>
        )}

      </form>
    </DialogShell>
  );
}
